import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import SearchInput from "@/components/SearchInput";
import { useSearch } from "@/lib/search";

/**
 * Pick one row out of a long table, by searching for it.
 *
 * MultiSelect's single-valued sibling. A Radix Select lists forty employees in a
 * scrolling column with no search box, and the register will hold hundreds; the
 * person picking a trainer knows a name or a code and wants to type it. Same shape
 * as MultiSelect — `options` is `[{ id, name, hint? }]`, one id in, one id out —
 * so the two look and search alike.
 */

const OPTION_FIELDS = ["name", "hint", "keywords"];

export default function EntityPicker({
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  empty = "Nothing to choose from yet.",
  searchPlaceholder = "Search…",
  disabled = false,
  className,
  testid,
  id,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const shown = useSearch(options, q, OPTION_FIELDS);
  const chosen = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  const pick = (id) => { onChange(id); setOpen(false); setQ(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} id={id}
                aria-label={placeholder} data-testid={testid} {...rest}
                className={cn("w-full justify-between font-normal", !chosen && "text-muted-foreground", className)}>
          <span className="truncate">{chosen ? chosen.name : placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {chosen && (
              <span role="button" tabIndex={0} aria-label={`Clear ${placeholder}`}
                    data-testid={testid ? `${testid}-clear` : undefined}
                    onClick={(e) => { e.stopPropagation(); onChange(""); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange(""); }
                    }}
                    className="p-0.5 rounded text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className="w-4 h-4 opacity-50" aria-hidden="true" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[280px] p-0" align="start">
        {options.length === 0 ? (
          <p className="px-3 py-4 text-sm text-muted-foreground" data-testid={testid ? `${testid}-empty` : undefined}>{empty}</p>
        ) : (
          <>
            <div className="p-2 border-b border-border">
              <SearchInput value={q} onChange={setQ} placeholder={searchPlaceholder}
                           testid={testid ? `${testid}-search` : undefined} />
            </div>
            <div className="max-h-72 overflow-y-auto py-1" role="listbox">
              {shown.slice(0, 200).map((o) => (
                <button type="button" key={o.id} role="option" aria-selected={o.id === value}
                        onClick={() => pick(o.id)}
                        data-testid={testid ? `${testid}-option-${o.id}` : undefined}
                        className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted",
                                      o.id === value && "bg-primary/5")}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{o.name}</span>
                    {o.hint && <span className="block text-xs text-muted-foreground truncate">{o.hint}</span>}
                  </span>
                  {o.id === value && <Check className="w-4 h-4 text-primary shrink-0" />}
                </button>
              ))}
              {shown.length === 0 && (
                <p className="px-3 py-4 text-sm text-muted-foreground">Nothing matches "{q}".</p>
              )}
              {shown.length > 200 && (
                <p className="px-3 py-2 text-xs text-muted-foreground">Showing 200 of {shown.length} — keep typing.</p>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
